import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { apiService } from '@/services/api';
import type { ProductUpdateDto } from '@/types';

/**
 * Header bell: product updates published by marketing (channel "app", service openbanking or
 * "all"), served per user by the shared Notification service through the BFF. The service picks
 * the locale, the kind labels and which posts count as unread; this only renders them. An item
 * is marked read when clicked (and its link opens), or all at once with "Mark all read".
 */

const POLL_MS = 5 * 60 * 1000;

const KIND_CLASS: Record<string, string> = {
  feature: 'bg-primary/10 text-primary',
  improvement: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  fix: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  notice: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const browserLocale = () => (typeof navigator !== 'undefined' && navigator.language) || 'pt';

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(browserLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<ProductUpdateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(() => {
    apiService
      .getProductUpdatesUnreadCount()
      .then((res) => { if (!res.isError && typeof res.value === 'number') setUnread(res.value); })
      .catch(() => { /* the bell stays quiet when updates are unavailable */ });
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = window.setInterval(refreshCount, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    apiService
      .getProductUpdates(browserLocale())
      .then((res) => {
        if (cancelled || res.isError || !res.value) return;
        setItems(res.value.items ?? []);
        setUnread(res.value.unreadCount ?? 0);
      })
      .catch(() => { /* empty state */ })
      .finally(() => { if (!cancelled) { setLoading(false); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const openItem = (item: ProductUpdateDto) => {
    if (item.isUnread) {
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, isUnread: false } : i)));
      setUnread((n) => Math.max(0, n - 1));
      apiService.markProductUpdateRead(item.id).catch(() => { /* re-synced on the next poll */ });
    }
    if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer');
  };

  const markAllRead = () => {
    setItems((list) => list.map((i) => ({ ...i, isUnread: false })));
    setUnread(0);
    apiService.markAllProductUpdatesRead().catch(() => { /* re-synced on the next poll */ });
  };

  const badge = unread > 99 ? '99+' : String(unread);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors"
      >
        <Icon name="notifications" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-[18px] text-center">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Product updates"
          className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">What's new</h2>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs font-medium text-primary hover:underline">
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && !loaded && (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Icon name="progress_activity" className="animate-spin" />
              </div>
            )}

            {loaded && items.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                No updates yet. New features and fixes will show up here.
              </p>
            )}

            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => openItem(item)}
                className={`w-full text-left px-4 py-3 flex gap-3 border-b last:border-b-0 border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors ${
                  item.isUnread ? 'bg-primary/5' : ''
                }`}
              >
                <span className={`mt-1.5 w-2 h-2 flex-shrink-0 rounded-full ${item.isUnread ? 'bg-primary' : 'bg-transparent'}`} aria-hidden />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${KIND_CLASS[item.kind] ?? KIND_CLASS.notice}`}>
                      {item.kindLabel || item.kind}
                    </span>
                    <span className="text-xs text-slate-400">{formatDate(item.date)}</span>
                  </span>
                  <span className={`block text-sm text-slate-800 dark:text-slate-200 ${item.isUnread ? 'font-semibold' : 'font-medium'}`}>
                    {item.title}
                  </span>
                  {item.summary && (
                    <span className="block mt-0.5 text-xs text-slate-500 dark:text-slate-400 line-clamp-3">{item.summary}</span>
                  )}
                  {item.image && (
                    <img src={item.image} alt="" loading="lazy" className="mt-2 w-full max-h-32 object-cover rounded" />
                  )}
                  {item.url && (
                    <span className="inline-block mt-1.5 text-xs font-medium text-primary">{item.ctaLabel || 'Read more'} →</span>
                  )}
                </span>
              </button>
            ))}
          </div>

          <div className="px-4 py-2.5 border-t border-slate-200 dark:border-slate-800">
            <Link
              to="/settings/notifications"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary"
            >
              <Icon name="settings" size={14} />
              Notification settings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
